import type { RunReadFailure } from "@/api/previewInsights";
import { QueryErrorState } from "@/api/QueryErrorState";
import { shortHash } from "@/lib/format";
import {
  DeniedState,
  EmptyState,
  ErrorState,
  OfflineState,
} from "@/ui/states";

/**
 * The parts the Preview Insights run page (#1098) and the panels rendered
 * inside it (#1099's Prompt Inspector) share: the failure surfaces every
 * read under `/preview-runs/{ref}` can end on, a fact cell, and a machine
 * identifier. Moved here from `ProjectPreviewRunRoute.tsx` unchanged in
 * behaviour so a second read on the same page does not grow a second copy.
 */

/** Which read failed. The contract keeps the answers apart; the sentences name the subject. */
export type ReadSubject = "run" | "prompt_call";

const READ_SUBJECT_COPY: Record<
  ReadSubject,
  {
    plural: string;
    deniedTitle: string;
    notFoundTitle: string;
    notFoundDescription: string;
    errorTitle: string;
  }
> = {
  run: {
    plural: "Preview runs",
    deniedTitle: "This preview run is not available to your account",
    notFoundTitle: "Preview run not available",
    notFoundDescription:
      "No preview run is available at this address for your account in this environment. The link may be out of date, the run may belong to a different project or deployment, or its preview target may no longer hold it.",
    errorTitle: "This preview run could not be read",
  },
  prompt_call: {
    plural: "Prompt calls",
    deniedTitle: "This prompt call is not available to your account",
    notFoundTitle: "Prompt call not available",
    // ONE generic answer covers an event this run does not hold as well as
    // every reason the run itself cannot be read; the sentence names every
    // possibility and settles none (ADR 0013 N2).
    notFoundDescription:
      "No prompt call is available at this address for your account in this environment. The link may be out of date, the run may belong to a different project or deployment, its preview target may no longer hold it, or the run may not hold this event.",
    errorTitle: "This prompt call could not be read",
  },
};

/**
 * The three failure surfaces the read can end on, kept apart the way the
 * contract keeps them apart. 401 and 501 are delegated to `QueryErrorState`,
 * which owns the sign-in and not-built recoveries for every screen.
 */
export function PreviewReadFailureState({
  subject,
  failure,
  error,
  onRetry,
}: {
  /** Which read failed — the sentences name it. */
  subject: ReadSubject;
  failure: RunReadFailure;
  error: unknown;
  onRetry: () => void;
}) {
  const copy = READ_SUBJECT_COPY[subject];
  // No second "Back to project" here: the page header already carries the
  // one way back, and two controls with one name on one screen is the
  // repeated-ambiguous-control defect `navModel.ts` records.
  switch (failure.kind) {
    case "denied":
      return (
        <DeniedState
          title={copy.deniedTitle}
          description="Your account is not authorized to inspect preview runs in this project in this environment. Nothing about the run has been read."
        />
      );
    case "not_found":
      // ONE generic answer, rendered as one generic sentence. The service
      // collapses an unknown locator, another Project's run, another
      // environment's, a binding the target no longer holds and a moved
      // candidate onto this response BY DESIGN, and the browser must not
      // pretend to know which — naming a reason would be inventing one.
      return (
        <EmptyState
          title={copy.notFoundTitle}
          description={copy.notFoundDescription}
        />
      );
    case "upstream":
      return failure.recovery === "retry" ? (
        <ErrorState
          title="The preview target took too long to answer"
          description="The read did not complete in time. This says nothing about the run itself — the same request may well succeed."
          onRetry={onRetry}
        />
      ) : (
        <ErrorState
          title="The preview target could not answer"
          description="The recorded preview target is unreachable, is not serving Preview Insights in this deployment, or answered something this Editor build cannot validate. This says nothing about the run itself; an identical request is refused until an operator acts, and you can try again after that."
          onRetry={onRetry}
        />
      );
    case "error":
      return (
        <ErrorState
          title={copy.errorTitle}
          description={`The Editor Service answered with "${failure.code}"${
            failure.requestId ? ` (request ${failure.requestId})` : ""
          }.`}
          onRetry={onRetry}
        />
      );
    case "transport":
      // No answer arrived, so nothing here is a statement by the service —
      // the same surface the pending branch renders for a paused query.
      return (
        <OfflineState
          title="The Editor Service could not be reached"
          description="No response arrived for this read. Nothing about the run has been read; check the connection and try again."
          onRetry={onRetry}
        />
      );
    case "unauthenticated":
    case "not_implemented":
      return (
        <QueryErrorState
          error={error}
          subject={copy.plural}
          title={copy.errorTitle}
          onRetry={onRetry}
        />
      );
  }
}


export function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 break-words text-foreground">{children}</dd>
    </div>
  );
}


/** A machine identifier: monospace, left-to-right, shortened when it is a long digest. */
export function Identifier({ value }: { value: string }) {
  const digest = /^(?:sha(?:256|512):)?[0-9a-f]{20,}$/i.test(value);
  return (
    <code
      dir="ltr"
      className="break-all font-mono text-xs"
      {...(digest ? { title: value } : {})}
    >
      {digest ? shortHash(value.replace(/^sha(?:256|512):/i, "")) : value}
    </code>
  );
}

